import React, { useState } from 'react';
import useUserStore from '../../../services/useUserStore';

const ROLES = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Gestor' },
  { value: 'OPERATOR', label: 'Operador' },
  { value: 'FINANCE', label: 'Financeiro' }
];

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validatePassword = (password) => {
  if (!password) return null; // Password is optional in edit

  if (password.length < 8) {
    return 'A senha deve ter no mínimo 8 caracteres';
  }
  if (!/[A-Z]/.test(password)) {
    return 'A senha deve conter pelo menos uma letra maiúscula';
  }
  if (!/[a-z]/.test(password)) {
    return 'A senha deve conter pelo menos uma letra minúscula';
  }
  if (!/[0-9]/.test(password)) {
    return 'A senha deve conter pelo menos um número';
  }
  return null;
};

const sanitizeString = (str) => {
  return str.replace(/[<>]/g, '');
};

const EditUserModal = ({ user, onClose, onSuccess }) => {
  const { updateUser } = useUserStore();
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    password: '',
    role: user.role || 'OPERATOR'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'O nome deve ter no mínimo 2 caracteres';
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    if (!formData.role) {
      newErrors.role = 'Selecione um perfil';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      const sanitizedData = {
        name: sanitizeString(formData.name.trim()),
        email: formData.email.toLowerCase().trim(),
        role: formData.role
      };

      // Only include password if it was changed
      if (formData.password) {
        sanitizedData.password = formData.password;
      }

      await updateUser(user.id, sanitizedData);
      onSuccess();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Falha ao atualizar usuário. Tente novamente.';
      setSubmitError(errorMessage);

      if (errorMessage.includes('email')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Editar Usuário</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {submitError && (
            <div className="co-error" style={{ marginBottom: '1rem' }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Nome Completo *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={errors.name ? 'error' : ''}
                required
              />
              {errors.name && <div className="error-text">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                required
              />
              {errors.email && <div className="error-text">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Nova Senha (opcional)</label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={errors.password ? 'error' : ''}
                placeholder="Deixe em branco para manter a senha atual"
              />
              {errors.password && <div className="error-text">{errors.password}</div>}
              <small style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                Mínimo 8 caracteres, com maiúsculas, minúsculas e números
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="role">Perfil *</label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className={errors.role ? 'error' : ''}
                required
              >
                {ROLES.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && <div className="error-text">{errors.role}</div>}
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
